import React from "react";
import { Badge } from "react-bootstrap";
import {
  FaBookOpen, FaHourglassHalf, FaCalculator, FaHashtag
} from "react-icons/fa";
import GenericCrudPage from '../components/GenericCrudPage'; // Assuming this path is correct

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Validation for Credit Hours form
const validateCreditHoursForm = (form) => {
  const errors = {};
  if (!form.exCreditHourTitle.trim()) errors.exCreditHourTitle = "Credit Hour Title is required";
  if (form.exCreditHourTitle.length > 100) errors.exCreditHourTitle = "Title must be less than 100 characters";

  // Validate numeric fields
  const numericFields = [
    { name: "exCreditHourTheory", label: "Theory Hours" },
    { name: "exCreditHourPractical", label: "Practical Hours" },
    { name: "exTotalMarks", label: "Total Marks" },
    { name: "exPassingGradePoints", label: "Passing Grade Points" },
    { name: "exExternalMarks", label: "External Marks" },
    { name: "exExternalPassingMarks", label: "External Passing Marks" },
    { name: "exNoOfTopics", label: "Number of Topics" },
  ];

  numericFields.forEach(field => {
    if (form[field.name] && isNaN(Number(form[field.name]))) {
      errors[field.name] = `${field.label} must be a number`;
    } else if (form[field.name] && Number(form[field.name]) < 0) {
      errors[field.name] = `${field.label} cannot be negative`;
    }
  });

  return errors;
};

// Configuration for Credit Hours CRUD operations
const creditHoursConfig = {
  baseUrl: BASE_URL,
  endpoints: {
    fetch: '/master/getCreditHours',
    save: '/master/saveCreditHours',
    update: '/master/updateCreditHours',
    delete: '/master/deleteCreditHours'
  },
  validateForm: validateCreditHoursForm,
  initialFormState: {
    exCreditHourTitle: "",
    exCreditHourTheory: "",
    exCreditHourPractical: "",
    exTotalMarks: "",
    exPassingGradePoints: "",
    exExternalMarks: "",
    exExternalPassingMarks: "",
    exNoOfTopics: "",
    exIsActive: 1,
    user: "",
  },
  entityName: 'Credit Hour Record'
};

// Form fields configuration
const formFields = [
  { name: "exCreditHourTitle", label: "Credit Hour Title", icon: FaBookOpen, required: true },
  { name: "exCreditHourTheory", label: "Theory Hours", icon: FaHourglassHalf, type: "number" },
  { name: "exCreditHourPractical", label: "Practical Hours", icon: FaHourglassHalf, type: "number" },
  { name: "exTotalMarks", label: "Total Marks", icon: FaCalculator, type: "number" },
  { name: "exPassingGradePoints", label: "Passing Grade Points", icon: FaCalculator, type: "number" },
  { name: "exExternalMarks", label: "External Marks", icon: FaCalculator, type: "number" },
  { name: "exExternalPassingMarks", label: "External Passing Marks", icon: FaCalculator, type: "number" },
  { name: "exNoOfTopics", label: "Number of Topics", icon: FaHashtag, type: "number" },
];

// Table columns configuration
const tableColumns = [
  {
    key: 'exCreditHourTitle',
    label: 'Title',
    sortable: true,
    render: (item) => (
      <div className="fw-bold">{item.exCreditHourTitle}</div>
    )
  },
  { key: 'exCreditHourTheory', label: 'Theory', sortable: true, render: (item) => item.exCreditHourTheory || '-' },
  { key: 'exCreditHourPractical', label: 'Practical', sortable: true, render: (item) => item.exCreditHourPractical || '-' },
  { key: 'exTotalMarks', label: 'Total Marks', sortable: true, render: (item) => item.exTotalMarks || '-' },
];

// CSV export headers
const csvHeaders = [
  { key: 'exCreditHourTitle', label: 'Credit Hour Title' },
  { key: 'exCreditHourTheory', label: 'Theory Hours' },
  { key: 'exCreditHourPractical', label: 'Practical Hours' },
  { key: 'exTotalMarks', label: 'Total Marks' },
  { key: 'exPassingGradePoints', label: 'Passing Grade Points' },
  { key: 'exExternalMarks', label: 'External Marks' },
  { key: 'exExternalPassingMarks', label: 'External Passing Marks' },
  { key: 'exNoOfTopics', label: 'Number of Topics' },
  { key: 'exIsActive', label: 'Status', formatter: (value) => (value ? "Active" : "Inactive") }
];

// Transform form data to API payload
// The userId is passed from GenericCrudPage
const transformPayload = (form, editId, userId) => ({
  exCreditHourTitle: form.exCreditHourTitle.trim(),
  exCreditHourTheory: parseFloat(form.exCreditHourTheory || 0),
  exCreditHourPractical: parseFloat(form.exCreditHourPractical || 0),
  exTotalMarks: parseFloat(form.exTotalMarks || 0),
  exPassingGradePoints: parseFloat(form.exPassingGradePoints || 0),
  exExternalMarks: parseFloat(form.exExternalMarks || 0),
  exExternalPassingMarks: parseFloat(form.exExternalPassingMarks || 0),
  exNoOfTopics: parseInt(form.exNoOfTopics || 0),
  exIsActive: form.exIsActive,
  user: userId,
  ...(editId && { id: editId })
});

// Transform API data to form
const transformToForm = (item) => ({
  exCreditHourTitle: item.exCreditHourTitle || "",
  exCreditHourTheory: item.exCreditHourTheory,
  exCreditHourPractical: item.exCreditHourPractical,
  exTotalMarks: item.exTotalMarks,
  exPassingGradePoints: item.exPassingGradePoints,
  exExternalMarks: item.exExternalMarks,
  exExternalPassingMarks: item.exExternalPassingMarks,
  exNoOfTopics: item.exNoOfTopics,
  exIsActive: item.exIsActive,
  user: item.user || "",
});

// Get view modal fields
const getViewFields = (creditHour) => {
  if (!creditHour) return [];
  return [
    { label: "Credit Hour Title", value: creditHour.exCreditHourTitle, icon: FaBookOpen },
    { label: "Theory Hours", value: creditHour.exCreditHourTheory, icon: FaHourglassHalf },
    { label: "Practical Hours", value: creditHour.exCreditHourPractical, icon: FaHourglassHalf },
    { label: "Total Marks", value: creditHour.exTotalMarks, icon: FaCalculator },
    { label: "Passing Grade Points", value: creditHour.exPassingGradePoints, icon: FaCalculator },
    { label: "External Marks", value: creditHour.exExternalMarks, icon: FaCalculator },
    { label: "External Passing Marks", value: creditHour.exExternalPassingMarks, icon: FaCalculator },
    { label: "Number of Topics", value: creditHour.exNoOfTopics, icon: FaHashtag },
  ];
};

export default function CreditHoursPage() {
  return (
    <GenericCrudPage
      title="Credit Hours" // This will be the header text for the page
      config={creditHoursConfig}
      formFields={formFields}
      tableColumns={tableColumns}
      emptyIcon={FaBookOpen}
      searchPlaceholder="Search by title, marks, topics..."
      csvHeaders={csvHeaders}
      csvFilename="credit_hours_export.csv"
      transformPayload={transformPayload}
      transformToForm={transformToForm}
      getViewFields={getViewFields}
    />
  );
}